import { BadgeEndpointDescriptor, BadgeGeometryReference, endpointIdentity, resolveBadgeAttachments } from './badgeAttachments'
import { BadgeGeometry, BadgeSide, getBadgeAttachmentPointFromGeometry, getBadgeGeometry } from './badgeGeometry'
import { RoutePoint, routeOrthogonalConnection } from './connectionGeometry'
import { ConnectionRoutingSettings, DEFAULT_CONNECTION_ROUTING_SETTINGS } from './connectionRoutingSettings'

export interface GlobalRoute {
    id: string
    from: any
    to: any
    points: RoutePoint[]
    status: 'resolved' | 'unresolved'
    conflicts: string[]
}

type Segment = { a: RoutePoint; b: RoutePoint; horizontal: boolean }

function segments(points: RoutePoint[]): Segment[] {
    return points.slice(1).map((point, index) => ({
        a: points[index], b: point, horizontal: points[index].y === point.y,
    }))
}

function between(value: number, a: number, b: number): boolean {
    return value >= Math.min(a, b) && value <= Math.max(a, b)
}

function samePoint(a: RoutePoint, b: RoutePoint): boolean {
    return a.x === b.x && a.y === b.y
}

function rangesOverlap(a1: number, a2: number, b1: number, b2: number): boolean {
    return Math.max(Math.min(a1, a2), Math.min(b1, b2))
        <= Math.min(Math.max(a1, a2), Math.max(b1, b2))
}

export function routesConflict(
    left: RoutePoint[],
    right: RoutePoint[],
    connectionSpacing = DEFAULT_CONNECTION_ROUTING_SETTINGS.connection_spacing,
): boolean {
    for (const l of segments(left)) for (const r of segments(right)) {
        if (l.horizontal !== r.horizontal) {
            const horizontal = l.horizontal ? l : r
            const vertical = l.horizontal ? r : l
            if (between(vertical.a.x, horizontal.a.x, horizontal.b.x)
                && between(horizontal.a.y, vertical.a.y, vertical.b.y)) return true
        } else if (l.horizontal && l.a.y === r.a.y) {
            const start = Math.max(Math.min(l.a.x, l.b.x), Math.min(r.a.x, r.b.x))
            const end = Math.min(Math.max(l.a.x, l.b.x), Math.max(r.a.x, r.b.x))
            if (end >= start) return true
        } else if (l.horizontal && r.horizontal
            && Math.abs(l.a.y - r.a.y) < connectionSpacing
            && rangesOverlap(l.a.x, l.b.x, r.a.x, r.b.x)) {
            return true
        } else if (!l.horizontal && l.a.x === r.a.x) {
            const start = Math.max(Math.min(l.a.y, l.b.y), Math.min(r.a.y, r.b.y))
            const end = Math.min(Math.max(l.a.y, l.b.y), Math.max(r.a.y, r.b.y))
            if (end >= start) return true
        } else if (!l.horizontal && !r.horizontal
            && Math.abs(l.a.x - r.a.x) < connectionSpacing
            && rangesOverlap(l.a.y, l.b.y, r.a.y, r.b.y)) {
            return true
        }
    }
    return false
}

function candidateRoutes(from: any, to: any): RoutePoint[][] {
    return [routeOrthogonalConnection(from.point, from.side, to.point, to.side)]
}

function routeLength(points: RoutePoint[]): number {
    return points.slice(1).reduce((sum, point, index) => sum
        + Math.abs(point.x - points[index].x) + Math.abs(point.y - points[index].y), 0)
}

export function routeConnectionsGlobally(
    endpoints: BadgeEndpointDescriptor[],
    badges: BadgeGeometryReference[],
    settings: ConnectionRoutingSettings = DEFAULT_CONNECTION_ROUTING_SETTINGS,
): GlobalRoute[] {
    const attachments = resolveBadgeAttachments(endpoints, badges, settings)
    const byIdentity = new Map(attachments.map(item => [item.identity, item]))
    const ids = [...new Set(endpoints.map(endpoint => endpoint.connectionId))].sort()
    const geometryById = new Map<string, BadgeGeometry>(badges.map(badge => [badge.id,
        'centerX' in badge.geometry ? badge.geometry : getBadgeGeometry(badge.geometry)]))
    const candidateSets: Array<Array<GlobalRoute & {
        bends: number; length: number; sideChanges: number; index: number; quality: number
    }>> = []
    ids.forEach(id => {
        const from = byIdentity.get(endpointIdentity({ connectionId: id, role: 'from' } as any))
        const to = byIdentity.get(endpointIdentity({ connectionId: id, role: 'to' } as any))
        if (!from || !to) return
        const fromDescriptor = endpoints.find(endpoint => endpoint.connectionId === id && endpoint.role === 'from')!
        const toDescriptor = endpoints.find(endpoint => endpoint.connectionId === id && endpoint.role === 'to')!
        const sides: BadgeSide[] = ['top', 'right', 'bottom', 'left']
        const fromSides = fromDescriptor.requestedSide === 'auto' || !fromDescriptor.requestedSide ? sides : [from.side]
        const toSides = toDescriptor.requestedSide === 'auto' || !toDescriptor.requestedSide ? sides : [to.side]
        const variants: Array<{ from: any; to: any; points: RoutePoint[]; index: number; sideChanges: number }> = []
        for (const fromSide of fromSides) for (const toSide of toSides) {
            const fromGeometry = geometryById.get(fromDescriptor.badgeId)!
            const toGeometry = geometryById.get(toDescriptor.badgeId)!
            const variantFrom = { ...from, side: fromSide,
                point: getBadgeAttachmentPointFromGeometry(
                    fromGeometry, fromSide, from.slot.index, from.slot.count, settings.connection_spacing,
                ) }
            const variantTo = { ...to, side: toSide,
                point: getBadgeAttachmentPointFromGeometry(
                    toGeometry, toSide, to.slot.index, to.slot.count, settings.connection_spacing,
                ) }
            candidateRoutes(variantFrom, variantTo).forEach((points, index) => variants.push({
                from: variantFrom, to: variantTo, points, index,
                sideChanges: Number(fromSide !== from.side) + Number(toSide !== to.side),
            }))
        }
        const scored = variants.map(variant => {
            const bends = Math.max(0, variant.points.length - 2)
            const length = routeLength(variant.points)
            return { id, ...variant, conflicts: [], status: 'resolved' as const, bends, length,
                quality: length + bends * 24 + variant.sideChanges * 80 }
        }).sort((a, b) => a.quality - b.quality || a.sideChanges - b.sideChanges
            || a.length - b.length || a.bends - b.bends || a.index - b.index
            || String(a.from.side).localeCompare(String(b.from.side)) || String(a.to.side).localeCompare(String(b.to.side)))
        candidateSets.push(scored)
    })

    let visited = 0
    const selected: GlobalRoute[] = []
    let best: GlobalRoute[] | null = null
    let bestQuality = Number.POSITIVE_INFINITY
    function select(index: number, quality: number): void {
        if (index === candidateSets.length) {
            if (quality < bestQuality) {
                bestQuality = quality
                best = [...selected]
            }
            return
        }
        for (const candidate of candidateSets[index]) {
            if (++visited > 100000) return
            if (quality + candidate.quality >= bestQuality) continue
            if (selected.some(route => routesConflict(candidate.points, route.points, settings.connection_spacing))) continue
            selected.push(candidate)
            select(index + 1, quality + candidate.quality)
            selected.pop()
        }
    }
    const shortest = candidateSets.map(candidates => [...candidates].sort((a, b) => a.length - b.length
        || a.bends - b.bends || a.sideChanges - b.sideChanges || a.index - b.index)[0])
    const withConflictStatus = (routes: typeof shortest): GlobalRoute[] => routes.map(route => {
        const conflicts = routes.filter(other => other.id !== route.id
            && routesConflict(route.points, other.points, settings.connection_spacing))
            .map(other => other.id).sort()
        return { id: route.id, from: route.from, to: route.to, points: route.points,
            status: conflicts.length ? 'unresolved' : 'resolved', conflicts }
    })
    select(0, 0)
    if (best) {
        return (best as GlobalRoute[]).map(route => ({
            id: route.id, from: route.from, to: route.to, points: route.points,
            status: route.status, conflicts: route.conflicts,
        }))
    }

    // Preserve deterministic diagnostic geometry when no crossing-free combination exists.
    return withConflictStatus(shortest)
}

export function assertRoutesCrossingFree(
    routes: GlobalRoute[],
    connectionSpacing = DEFAULT_CONNECTION_ROUTING_SETTINGS.connection_spacing,
): boolean {
    return routes.every((route, index) => routes.slice(index + 1)
        .every(other => !routesConflict(route.points, other.points, connectionSpacing)))
}
